/**
 * The project chat for the editor: whether its column is open, the session of the project on screen and who can be
 * talked to. The editor attaches what it has — the demo participants, a shared session, or nobody — and the chat
 * follows: the demo gets its made-up relay (`demo.ts`), a shared session waits for the rt relay to carry the chat
 * (docs/tasks/project-chat), and with nobody else in the project the column explains how people get there.
 */
import type { DemoPresence } from '../demoBots';
import type { ParticipantLook } from '../presenceSource';
import { DemoChatTransport, type DemoChatNetwork } from './demo';
import { ChatSession } from './session';

export type ChatAvailability = 'none' | 'demo' | 'unavailable';
export type ChatMember = { id: string; name: string };
/** What the composer holds for a project while the column is closed: the text, and the message it answers or edits. */
export type ChatDraft = { text: string; replyTo: string | null; editing: string | null };
export type ProjectChatState = {
  readonly open: boolean;
  readonly availability: ChatAvailability;
  readonly session: ChatSession | null;
  /** The others the chat is with, as the presence lists them now. */
  readonly members: readonly ChatMember[];
  readonly projectId: string | null;
  /** Counts the times the composer was addressed to someone (`address`), so an open composer takes the new draft. */
  readonly addressed: number;
};
const EMPTY_DRAFT: ChatDraft = { text: '', replyTo: null, editing: null };

/** The made-up network of the demo relay, for checking the chat's slow and failing states (development only). */
function devNetwork(): DemoChatNetwork {
  if (!import.meta.env?.DEV || typeof location === 'undefined') return 'normal';
  const value = new URLSearchParams(location.search).get('chatNetwork');
  return value === 'slow' || value === 'offline' ? value : 'normal';
}
const storage = () => { try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; } };

class ProjectChat {
  private state: ProjectChatState = { open: false, availability: 'none', session: null, members: [], projectId: null, addressed: 0 };
  private listeners = new Set<() => void>();
  private drafts = new Map<string, ChatDraft>();
  private looks: ((userId: string) => ParticipantLook | null) | null = null;
  private transport: DemoChatTransport | null = null;

  get = (): ProjectChatState => this.state;
  isOpen = (): boolean => this.state.open;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private set(patch: Partial<ProjectChatState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  setOpen(open: boolean): void { if (open !== this.state.open) this.set({ open }); }

  /** How a participant is drawn in the chat: as the presence draws them, else as a person in a colour of their own. */
  look(userId: string): ParticipantLook | null { return this.looks?.(userId) ?? null; }

  /**
   * The project on screen and who else is in it. Returns the detach, which ends the session it started; a later
   * attach replaces an earlier one, so the editor can call it on every change.
   */
  attach(input: { projectId: string; demo: DemoPresence | null; shared: boolean }): () => void {
    this.state.session?.dispose();
    this.transport = null;
    let stopMembers: (() => void) | null = null, session: ChatSession | null = null;
    if (input.demo) {
      const demo = input.demo;
      const people = () => demo.members().filter(member => !member.agent && member.online).map(member => ({ id: member.id, name: member.name }));
      this.transport = new DemoChatTransport({ projectId: input.projectId, people, storage: storage(), network: devNetwork() });
      session = new ChatSession({ transport: this.transport });
      this.looks = userId => demo.look(userId);
      stopMembers = demo.subscribeMembers(() => { if (this.state.session === session) this.set({ members: people() }); });
      this.set({ availability: 'demo', session, members: people(), projectId: input.projectId });
      session.start();
    } else {
      this.looks = null;
      this.set({ availability: input.shared ? 'unavailable' : 'none', session: null, members: [], projectId: input.projectId });
    }
    return () => {
      stopMembers?.();
      if (session && this.state.session === session) {
        session.dispose(); this.transport = null; this.looks = null;
        this.set({ availability: 'none', session: null, members: [] });
      } else session?.dispose();
    };
  }

  draft(projectId: string | null): ChatDraft { return (projectId && this.drafts.get(projectId)) || EMPTY_DRAFT; }
  setDraft(projectId: string | null, draft: ChatDraft): void {
    if (!projectId) return;
    if (!draft.text && !draft.replyTo && !draft.editing) this.drafts.delete(projectId); else this.drafts.set(projectId, draft);
  }

  /**
   * Puts «@Name » at the start of the composer, a message to everyone that names one of them, and never as a reply; the
   * caller opens the chat. An edit of a sent message under way is kept as it is: the composer only takes the focus.
   */
  address(name: string): void {
    const projectId = this.state.projectId, mention = `@${name} `;
    if (!projectId) return;
    const draft = this.draft(projectId);
    if (!draft.editing) this.setDraft(projectId, { text: draft.text.startsWith(mention) ? draft.text : mention + draft.text, replyTo: null, editing: null });
    this.set({ addressed: this.state.addressed + 1 });
  }

  /** The demo relay's network, for the dev console and the browser check. */
  setDemoNetwork(network: DemoChatNetwork): void { this.transport?.setNetwork(network); }
}

export const projectChat = new ProjectChat();
if (typeof window !== 'undefined' && import.meta.env?.DEV) (window as unknown as { hpChat?: ProjectChat }).hpChat = projectChat;
